import { useCallback, useEffect, useState } from "react";
import { endOfDay, format, startOfDay } from "date-fns";
import api, { errorMessage } from "./api";
import { AdminField, Card, ConfirmButton, Eyebrow, Feedback, Hint, PrimaryButton, SecondaryButton } from "./ui";

const toInputDate = (value) => (value ? format(new Date(value), "yyyy-MM-dd") : "");

const isActive = (popup, now = new Date()) => new Date(popup.startDate) <= now && new Date(popup.endDate) >= now;

const PopupForm = ({ popup, onDone, onCancel }) => {
    const [type, setType] = useState(popup.type || "text");
    const [text, setText] = useState(popup.text || "");
    const [file, setFile] = useState(null);
    const [startDate, setStartDate] = useState(toInputDate(popup.startDate));
    const [endDate, setEndDate] = useState(toInputDate(popup.endDate));
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (endDate < startDate) {
            setMessage({ error: true, text: "Das Ende liegt vor dem Beginn." });
            return;
        }
        if (type === "image" && !file && !popup._id) {
            setMessage({ error: true, text: "Bitte ein Bild auswählen." });
            return;
        }
        const data = new FormData();
        data.append("type", type);
        data.append("startDate", startOfDay(new Date(`${startDate}T00:00`)).toISOString());
        data.append("endDate", endOfDay(new Date(`${endDate}T00:00`)).toISOString());
        if (type === "text") data.append("text", text);
        if (type === "image" && file) data.append("image", file);

        setBusy(true);
        try {
            if (popup._id) {
                await api.put(`/popups/${popup._id}`, data);
            } else {
                await api.post("/popups", data);
            }
            onDone();
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Popup konnte nicht gespeichert werden.") });
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="grid gap-3 border-t border-forest/10 pt-3">
            <div role="radiogroup" aria-label="Art" className="flex gap-5 text-[15px]">
                {[
                    ["text", "Text"],
                    ["image", "Bild"],
                ].map(([value, label]) => (
                    <label key={value} className="flex min-h-[44px] items-center gap-2">
                        <input
                            type="radio"
                            name="popup-type"
                            checked={type === value}
                            onChange={() => setType(value)}
                            className="h-5 w-5 accent-forest"
                        />
                        {label}
                    </label>
                ))}
            </div>
            {type === "text" ? (
                <AdminField label="Text" as="textarea" rows={3} required value={text} onChange={(e) => setText(e.target.value)} />
            ) : (
                <AdminField
                    label={popup._id ? "Neues Bild (optional)" : "Bild"}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                />
            )}
            <div className="grid grid-cols-2 gap-2.5">
                <AdminField label="Von" type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <AdminField label="Bis (inkl.)" type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Feedback message={message} />
            <div className="grid grid-cols-2 gap-2.5">
                <PrimaryButton type="submit" disabled={busy}>
                    Speichern
                </PrimaryButton>
                <SecondaryButton onClick={onCancel}>Abbrechen</SecondaryButton>
            </div>
        </form>
    );
};

// Hinweis-Popups auf der Startseite. Angezeigt wird das neueste aktive.
export default function PopupsSection() {
    const [popups, setPopups] = useState([]);
    const [editing, setEditing] = useState(null);
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        const response = await api.get("/popups");
        setPopups(response.data);
    }, []);

    useEffect(() => {
        load().catch(() => setMessage({ error: true, text: "Popups konnten nicht geladen werden." }));
    }, [load]);

    const remove = async (popup) => {
        try {
            await api.delete(`/popups/${popup._id}`);
            await load();
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Löschen fehlgeschlagen.") });
        }
    };

    return (
        <Card className="grid gap-3 p-[18px]">
            <div className="flex items-center justify-between gap-3">
                <Eyebrow>Hinweis-Popup</Eyebrow>
                {!editing && (
                    <SecondaryButton className="!min-h-[40px] !py-2 text-[13px]" onClick={() => setEditing({})}>
                        + Neues Popup
                    </SecondaryButton>
                )}
            </div>
            <Hint>Erscheint auf der Startseite im Zeitraum. Bei mehreren aktiven gilt das zuletzt beginnende.</Hint>
            <Feedback message={message} />
            {editing && !editing._id && (
                <PopupForm
                    popup={editing}
                    onCancel={() => setEditing(null)}
                    onDone={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
            {popups.map((popup) => (
                <div key={popup._id} className="grid gap-2 border-t border-forest/10 pt-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[13px] text-forest/60">
                                {format(new Date(popup.startDate), "dd.MM.yyyy")} –{" "}
                                {format(new Date(popup.endDate), "dd.MM.yyyy")}
                                {isActive(popup) && <span className="ml-2 font-semibold text-erfolg">aktiv</span>}
                            </div>
                            {popup.type === "image" ? (
                                <img
                                    src={`/api/public/popups/${popup._id}/image`}
                                    alt="Popup-Bild"
                                    className="mt-1.5 h-20 w-auto border border-forest/10"
                                />
                            ) : (
                                <div className="mt-1 line-clamp-3 whitespace-pre-line text-[15px]">{popup.text}</div>
                            )}
                        </div>
                        {!editing && (
                            <div className="flex flex-none flex-col items-end gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => setEditing(popup)}
                                    className="bg-transparent py-1.5 text-[13px] underline"
                                >
                                    Bearbeiten
                                </button>
                                <ConfirmButton onConfirm={() => remove(popup)}>Löschen</ConfirmButton>
                            </div>
                        )}
                    </div>
                    {editing && editing._id === popup._id && (
                        <PopupForm
                            popup={popup}
                            onCancel={() => setEditing(null)}
                            onDone={() => {
                                setEditing(null);
                                load();
                            }}
                        />
                    )}
                </div>
            ))}
        </Card>
    );
}
